/**
 * Sprint 11 Phase 3d seed — 90-day nudge dot.
 *
 * Renders nothing for the first 89 days after `report_sent_at`. From day
 * 90 onward, surfaces a small colored dot next to the "Completed" line
 * in the IndividualClientView header. The dot has NO action attached
 * — clicking does not navigate, open a modal, or schedule anything.
 * It is a passive signal-generator.
 *
 * What we measure:
 *   * Hover (once per mount per coach session) — "did the coach
 *     notice the dot?"
 *   * Click — "did the coach reach for action?"
 *
 * If coaches never engage with the dot, the 90-day re-assessment
 * moment isn't real for them yet and Phase 3d longitudinal infra
 * should not be built. If they engage heavily, demand-led validation
 * for Phase 3d is in the bank. Signal arrives in months, not weeks
 * (zero engagements today; 90-day delay before any client crosses
 * the threshold). The value is patient.
 */
import React from "react";
import { api } from "../lib/api";

const NUDGE_THRESHOLD_DAYS = 90;

export default function NudgeDot({ reportSentAt, deploymentId, respondentId }) {
  const hoverFiredRef = React.useRef(false);

  const daysSince = React.useMemo(() => {
    if (!reportSentAt) return null;
    const sent = new Date(reportSentAt).getTime();
    if (isNaN(sent)) return null;
    return Math.floor((Date.now() - sent) / (1000 * 60 * 60 * 24));
  }, [reportSentAt]);

  if (daysSince === null || daysSince < NUDGE_THRESHOLD_DAYS) return null;

  // Fire-and-forget telemetry. We deliberately don't await — UX must
  // not depend on the analytics sink being up.
  function fire(kind) {
    api.post("/api/telemetry/event", {
      surface: "nudge_dot_90day",
      kind,
      deployment_id: deploymentId,
      respondent_id: respondentId,
      days_since_send: daysSince,
    }).catch(() => { /* analytics sink failure is non-critical */ });
  }

  function onHover() {
    if (hoverFiredRef.current) return;
    hoverFiredRef.current = true;
    fire("hover");
  }

  function onClick() {
    fire("click");
  }

  const tooltip =
    `Last reflection was sent ${daysSince} days ago. ` +
    `Common cadence for follow-up reflections is 90 days — ` +
    `but only if it feels right for the engagement.`;

  return (
    <span
      role="img"
      aria-label={tooltip}
      title={tooltip}
      data-testid="nudge-dot-90day"
      data-days-since={daysSince}
      onMouseEnter={onHover}
      onClick={onClick}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        marginLeft: 8,
        backgroundColor: "#D78E2C",  // warm amber — present, not urgent
        cursor: "pointer",
        verticalAlign: "middle",
      }}
    />
  );
}
